import React from 'react';
import { Info } from 'lucide-react';
import TrodoTooltip from 'shared/components/ui/TrodoTooltip';
import { getScoreMetricTooltip } from 'features/agent-analytics/components/scoreMetricTooltips';

// Per-metric label + scale direction. higher_is_better=false means lower score is healthier.
const METRIC = {
  contextual_grounding: { label: 'Contextual grounding', higher_is_better: true, min: 0, max: 1 },
  rule_adherence: { label: 'Rule adherence', higher_is_better: true, min: 0, max: 1 },
  logical_contradiction: { label: 'Contradiction P', higher_is_better: false, min: 0, max: 1 },
  trajectory_confabulation: { label: 'Trajectory alignment', higher_is_better: true, min: -1, max: 1 },
  factual_retention: { label: 'Factual faithfulness', higher_is_better: true, min: 0, max: 1 },
  semantic_echo: { label: 'Semantic echo', higher_is_better: false, min: 0, max: 1 },
  refusal_overreach: { label: 'Refusal score', higher_is_better: false, min: 0, max: 1 },
};

const PATTERN_ORDER = [
  'contextual_grounding',
  'rule_adherence',
  'logical_contradiction',
  'trajectory_confabulation',
  'factual_retention',
  'semantic_echo',
  'refusal_overreach',
];

function formatDecimal(value, maxDecimals = 2) {
  if (!Number.isFinite(value)) return null;
  return String(Number(value.toFixed(maxDecimals)));
}

function formatScore(score) {
  if (!Number.isFinite(score)) return '—';
  return formatDecimal(score) ?? '—';
}

function formatThreshold(threshold) {
  return formatDecimal(threshold);
}

function barFillPct(score, cfg) {
  if (!Number.isFinite(score)) return 0;
  const range = (cfg.max - cfg.min) || 1;
  const norm = (score - cfg.min) / range;
  const clamped = Math.max(0, Math.min(1, norm));
  return cfg.higher_is_better ? clamped * 100 : (1 - clamped) * 100;
}

function markerPct(threshold, cfg) {
  if (!Number.isFinite(threshold)) return null;
  const range = (cfg.max - cfg.min) || 1;
  const norm = (threshold - cfg.min) / range;
  return Math.max(0, Math.min(100, norm * 100));
}

/** Crisp 1px threshold line — same width on fill and track. */
function thresholdMarkerStyle(pct) {
  const p = Math.max(0, Math.min(100, pct));
  return {
    background: `linear-gradient(90deg, transparent calc(${p}% - 0.5px), var(--text-primary) calc(${p}% - 0.5px), var(--text-primary) calc(${p}% + 0.5px), transparent calc(${p}% + 0.5px))`,
  };
}

function isSkippedScore(score, detail) {
  const status = detail?.status;
  const skipReason = detail?.skip_reason;
  return status === 'skip' || (!Number.isFinite(Number(score)) && skipReason);
}

/**
 * Compact labeled bar for one generation_quality metric.
 * Score in header; threshold on bar marker. Hover bar for score + threshold tooltip.
 */
export default function ScoreBar({ pattern, score, threshold, crossed, detail }) {
  if (isSkippedScore(score, detail)) return null;

  const cfg = METRIC[pattern] || { label: pattern || 'Metric', higher_is_better: true, min: 0, max: 1 };

  const numScore = Number(score);
  const numThreshold = Number(threshold);
  const fillPct = barFillPct(numScore, cfg);
  const marker = markerPct(numThreshold, cfg);
  const fillColor = crossed ? 'var(--feedback-error)' : 'var(--feedback-success)';
  const metricTip = getScoreMetricTooltip(pattern);
  const thresholdText = formatThreshold(numThreshold);
  const barTooltip = thresholdText != null ? (
    <span className="flex flex-col gap-0.5 tabular-nums">
      <span>Score: {formatScore(numScore)}</span>
      <span>Threshold: {thresholdText}</span>
    </span>
  ) : (
    <span className="tabular-nums">Score: {formatScore(numScore)}</span>
  );

  return (
    <div className="flex flex-col gap-1" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex min-w-0 items-baseline gap-1.5 text-[11px]">
        <span className="truncate font-medium text-[color:var(--text-primary)]">{cfg.label}</span>
        {metricTip ? (
          <TrodoTooltip content={metricTip} placement="right">
            <span
              className="inline-flex shrink-0 items-center justify-center text-[color:var(--text-muted)] opacity-80 hover:opacity-100"
              aria-label="What this score means"
            >
              <Info className="h-3 w-3" strokeWidth={2.25} aria-hidden />
            </span>
          </TrodoTooltip>
        ) : null}
        <span className={`shrink-0 tabular-nums ${crossed ? 'font-semibold text-[color:var(--feedback-error)]' : 'font-semibold text-[color:var(--feedback-success)]'}`}>
          {formatScore(numScore)}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="w-5 shrink-0 text-right text-[9px] tabular-nums text-[color:var(--text-muted)]">
          {formatDecimal(cfg.min) ?? cfg.min}
        </span>
        <TrodoTooltip content={barTooltip} placement="top" className="block min-w-0 flex-1">
          <span className="block w-full">
            <div className="relative h-2 w-full overflow-hidden rounded-full bg-[color:var(--surface-sunken)]">
              <div
                className="absolute left-0 top-0 h-full"
                style={{ width: `${fillPct}%`, backgroundColor: fillColor }}
              />
              {marker != null ? (
                <div
                  className="pointer-events-none absolute inset-0 z-10"
                  style={thresholdMarkerStyle(marker)}
                />
              ) : null}
            </div>
          </span>
        </TrodoTooltip>
        <span className="w-5 shrink-0 text-left text-[9px] tabular-nums text-[color:var(--text-muted)]">
          {formatDecimal(cfg.max) ?? cfg.max}
        </span>
      </div>
    </div>
  );
}

export { PATTERN_ORDER, METRIC, isSkippedScore };
